import React from "react";

/**
 * Holland (RIASEC) result blocks, driven by `result.holland` from the backend
 * holland_code resolver: a code badge for the hero card and a hexagon profile.
 */

export const HOLLAND_ORDER = ["R", "I", "A", "S", "E", "C"];

const TYPE_NAMES = {
  R: { id: "Realistic", en: "Realistic" },
  I: { id: "Investigative", en: "Investigative" },
  A: { id: "Artistic", en: "Artistic" },
  S: { id: "Social", en: "Social" },
  E: { id: "Enterprising", en: "Enterprising" },
  C: { id: "Conventional", en: "Conventional" },
};

const ACCENT = "#D47A65";
const INK = "#264D4F";

export function hollandScoresInHexagonOrder(holland) {
  const byLetter = new Map((holland?.scores || []).map((s) => [s.letter, s]));
  if (!HOLLAND_ORDER.every((letter) => byLetter.has(letter))) return null;
  return HOLLAND_ORDER.map((letter) => byLetter.get(letter));
}

export function hollandNotes(holland, lang = "id") {
  if (!holland) return [];
  const id = lang !== "en";
  const notes = [];
  if (holland.profile === "LOW") {
    notes.push(id
      ? "Skor semua tipe minatmu masih rendah. Wajar kalau kamu belum menemukan bidang yang paling menarik. Coba eksplorasi pengalaman baru, lalu ulangi tes ini nanti."
      : "All six interest scores are still low. It is normal not to have found your field yet. Try new experiences and take the test again later.");
  } else if (holland.profile === "FLAT") {
    notes.push(id
      ? "Skor keenam tipe minatmu hampir sama. Minatmu luas, jadi coba eksplorasi beberapa bidang sebelum memilih satu arah."
      : "Your six interest scores are nearly equal. Your interests are broad, so explore a few fields before settling on one direction.");
  } else if (holland.consistency === "high") {
    notes.push(id
      ? "Minatmu searah: dua tipe teratasmu bersebelahan di hexagon Holland, jadi pilihan studi dan karier yang menggabungkan keduanya cenderung mudah ditemukan."
      : "Your interests point the same way: your top two types sit next to each other on Holland's hexagon, so studies and careers that combine them are easy to find.");
  } else if (holland.consistency === "medium") {
    notes.push(id
      ? "Dua tipe teratasmu cukup berdekatan di hexagon Holland. Kamu bisa menikmati peran yang memadukan keduanya."
      : "Your top two types are fairly close on Holland's hexagon. Roles that blend both are likely to suit you.");
  } else if (holland.consistency === "low") {
    notes.push(id
      ? "Kombinasi minatmu unik: dua tipe teratasmu berseberangan di hexagon Holland. Kamu mungkin menikmati peran yang memadukan dua dunia yang berbeda."
      : "Your combination is unusual: your top two types sit opposite each other on Holland's hexagon. You may enjoy roles that bridge two different worlds.");
  }
  const tied = holland.tied_codes || [];
  if (tied.length >= 2) {
    const list = tied.join(id ? " dan " : " and ");
    notes.push(id
      ? `Skor ${list} sama, jadi urutannya mengikuti urutan baku R-I-A-S-E-C.`
      : `${list} scored the same, so their order follows the standard R-I-A-S-E-C order.`);
  }
  return notes;
}

export function HollandCodeBadge({ holland, lang = "id", color }) {
  const letters = holland?.letters || [];
  if (!letters.length) return null;
  const tint = color || ACCENT;
  return (
    <div className="flex flex-col gap-1" data-testid="holland-code-badge">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {lang === "en" ? "Your Holland Code" : "Kode Holland-mu"}
      </span>
      <div className="flex items-center gap-1.5">
        {letters.map((letter, i) => (
          <span
            key={`${letter}-${i}`}
            className="inline-flex items-center justify-center min-w-9 h-9 px-2 rounded-xl font-display text-lg"
            style={i === 0 ? { background: tint, color: "#fff" } : { background: tint + "22", color: tint }}
            title={TYPE_NAMES[letter]?.[lang === "en" ? "en" : "id"] || letter}
          >
            {letter}
          </span>
        ))}
      </div>
    </div>
  );
}

function point(cx, cy, radius, index) {
  const angle = (-90 + index * 60) * (Math.PI / 180);
  return [cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)];
}

export function HollandHexagon({ holland, lang = "id" }) {
  const scores = hollandScoresInHexagonOrder(holland);
  if (!scores) return null;
  const top = new Set((holland.letters || []).slice(0, 3));
  // Wider than tall so side labels such as "Investigative" are never clipped.
  const width = 380;
  const height = 320;
  const cx = width / 2;
  const cy = height / 2;
  const maxR = 100;
  const rings = [0.25, 0.5, 0.75, 1];
  const ringPath = (f) => HOLLAND_ORDER.map((_, i) => point(cx, cy, maxR * f, i).join(",")).join(" ");
  const shape = scores.map((s, i) => point(cx, cy, maxR * Math.max(0.04, (s.percentage || 0) / 100), i).join(",")).join(" ");
  const nameKey = lang === "en" ? "en" : "id";

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-md mx-auto" role="img"
      aria-label={scores.map((s) => `${s.letter} ${Math.round(s.percentage || 0)}%`).join(", ")}
      data-testid="holland-hexagon">
      {rings.map((f) => (
        <polygon key={f} points={ringPath(f)} fill="none" stroke="currentColor" strokeOpacity={f === 1 ? 0.25 : 0.12} className="text-teal-800" />
      ))}
      {HOLLAND_ORDER.map((_, i) => {
        const [x, y] = point(cx, cy, maxR, i);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="currentColor" strokeOpacity={0.12} className="text-teal-800" />;
      })}
      <polygon points={shape} fill={ACCENT} fillOpacity={0.22} stroke={ACCENT} strokeWidth={2} strokeLinejoin="round" />
      {scores.map((s, i) => {
        const [x, y] = point(cx, cy, maxR * Math.max(0.04, (s.percentage || 0) / 100), i);
        return <circle key={s.letter} cx={x} cy={y} r={top.has(s.letter) ? 4.5 : 3} fill={top.has(s.letter) ? ACCENT : INK} />;
      })}
      {scores.map((s, i) => {
        const [x, y] = point(cx, cy, maxR + 16, i);
        const anchor = Math.abs(x - cx) < 4 ? "middle" : x > cx ? "start" : "end";
        // Top label sits above its corner, bottom label below it.
        const dy = Math.abs(x - cx) < 4 ? (y < cy ? -14 : 6) : -6;
        const highlighted = top.has(s.letter);
        return (
          <text key={s.letter} x={x} y={y + dy} textAnchor={anchor} dominantBaseline="middle"
            fill={highlighted ? ACCENT : INK} fillOpacity={highlighted ? 1 : 0.75}
            style={{ fontSize: 12, fontWeight: highlighted ? 700 : 500 }}>
            <tspan>{s.letter} · {Math.round(s.percentage || 0)}%</tspan>
            <tspan x={x} dy={14} style={{ fontSize: 10, fontWeight: 400 }}>{TYPE_NAMES[s.letter]?.[nameKey]}</tspan>
          </text>
        );
      })}
    </svg>
  );
}

export function HollandProfileSection({ holland, lang = "id" }) {
  if (!hollandScoresInHexagonOrder(holland)) return null;
  const notes = hollandNotes(holland, lang);
  return (
    <section className="mb-8 p-5 md:p-6 rounded-3xl bg-card border border-border" data-testid="holland-profile-section">
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <div className="font-display text-lg md:text-xl">{lang === "en" ? "Your Interest Profile" : "Profil Minatmu"}</div>
        <div className="text-[11px] text-muted-foreground">{lang === "en" ? "Score per RIASEC type" : "Skor per tipe RIASEC"}</div>
      </div>
      <div className="grid md:grid-cols-2 gap-6 items-center">
        <HollandHexagon holland={holland} lang={lang} />
        <div className="space-y-3">
          <HollandCodeBadge holland={holland} lang={lang} />
          {notes.map((note) => (
            <p key={note} className="text-sm text-teal-800/90 leading-relaxed" data-testid="holland-note">{note}</p>
          ))}
        </div>
      </div>
    </section>
  );
}
